import { JSX, useCallback, useContext, useState } from 'react'
import { useSelector } from 'react-redux'

import { getWTZDate } from 'core-components/helpers/date'
import { Button } from 'core-components/library/button'
import { TabButtons } from 'core-components/library/tabButtons'
import { Tooltip } from 'core-components/library/tooltip'
import { Controller } from 'core-components/library/validation'
import { FlexGridWrapperContext } from 'core-components/sitebuilder/layout/context/FlexGridWrapperContext'
import { useWidgetMetaData } from 'core-components/sitebuilder/layout/flexItem/hooks/useWidgetMetaData'
import { IRenderCustomSettingsParams } from 'core-components/sitebuilder/layout/flexItem/models/IRenderCustomContent'
import { useCurrentSitePage } from 'core-components/sitebuilder/layout/hooks/useCurrentSitePage'
import { IPageWidget, IWidget } from 'core-components/sitebuilder/models/IWidget'
import { IWidgetsData } from 'core-components/sitebuilder/models/IWidgetData'
import { TDetailState } from 'core-components/sitebuilder/settings/models/TState'
import { selectFlexSettings } from 'core-components/sitebuilder/store/selectors/selectFlexSettings'
import { selectRoles } from 'core-components/sitebuilder/store/selectors/selectRoles'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { selectWidgets } from 'core-components/sitebuilder/store/selectors/selectWidgets'

import { BasicSettings } from './detail/BasicSettings'
import { CustomSettings } from './detail/CustomSettings'
import { EditorSettings } from './detail/EditorSettings'
import { getWidgetData, prepareDefaultState } from './detail/helpers/prepareDefaultState'
import { useIsValid } from './detail/hooks/useIsValid'

import styles from './Detail.module.scss'

export interface IWidgetsListDetail {
  isUserSettings?: boolean
  widget: IWidget
  pageWidget?: IPageWidget
  widgetsData?: IWidgetsData[]
  onClose: () => void
  onChangeAndClose?: (
    pageWidgetSettings?: IPageWidget,
    additionalSettings?: any, //object
    redactorSettings?: any //object
  ) => void
  additionalSettings?: any //object
  hasAdditionalSettings?: boolean
  getWidgetAfterSaving?: (widget: IWidget) => void
  renderCustomSettingsWidget?: (data: IRenderCustomSettingsParams) => JSX.Element | null
}

export const WidgetsListDetail = (props: IWidgetsListDetail) => {
  const { saveWidget, addWidgetToFlexGrid } = useContext(FlexGridWrapperContext)
  const sitePage = useCurrentSitePage()

  const widgets = useSelector(selectWidgets)
  const roles = useSelector(selectRoles)
  const user = useSelector(selectUser)
  const flexSettings = useSelector(selectFlexSettings)

  const { meta } = useWidgetMetaData({ widgetId: props.widget.i })

  const [saving, setSaving] = useState<boolean>(false)
  const [widget, setWidget] = useState<IWidget>(props.widget)
  const [pageWidget, setPageWidget] = useState<IPageWidget>(props.pageWidget || ({} as IPageWidget))
  const [widgetData, setWidgetData] = useState<IWidgetsData | undefined>(() =>
    getWidgetData(props.widget, props.widgetsData)
  )
  const [additionalSettings, setAdditionalSettings] = useState<string>(() =>
    props.additionalSettings ? JSON.stringify(props.additionalSettings) : ''
  )
  const [redactorSettings, setRedactorSettings] = useState<any>(() =>
    prepareDefaultState(props.widget, sitePage, flexSettings)
  )

  const { errorMessage } = useIsValid({ widget })

  const onChangeField = useCallback(
    (field: string, value: any) => {
      if (
        props.isUserSettings &&
        (field === 'x' || field === 'y' || field === 'w' || field === 'h')
      ) {
        if (widgetData) {
          const newWidgetData = { ...widgetData, [field]: value }
          setWidgetData(newWidgetData)
        }
      } else {
        setWidget((widget) => {
          const newWidgetData = { ...widget, [field]: value }

          if (JSON.stringify(newWidgetData) !== JSON.stringify(widget)) {
            return newWidgetData
          }
          return widget
        })
      }
    },
    [props.isUserSettings, widgetData]
  )

  const onSubmit = async () => {
    setSaving(true)
    const savedWidget = await saveWidget(widget)

    if (!savedWidget) {
      setSaving(false)
      return
    }

    const newWidget = {
      ...widget,
      createdAt: getWTZDate().toString(),
      createdBy: user.email,
    }
    if (newWidget.isNew) {
      // @ts-ignore check it. why Id?
      newWidget.i = savedWidget.Id
    }

    addWidgetToFlexGrid(newWidget).catch(console.error)

    if (props.getWidgetAfterSaving) {
      props.getWidgetAfterSaving(newWidget)
    }
    if (props.onChangeAndClose) {
      const widgetsData = props.widgetsData
      let wData
      let wIndex = 0
      if (widgetsData) {
        wData = widgetsData.find((value, index) => {
          wIndex = index
          return props.widget.i === value.i
        })
        if (wData) {
          wData = Object.assign(wData, widgetData)
          widgetsData[wIndex] = wData
        }
      }

      props.onChangeAndClose(
        pageWidget,
        additionalSettings ? JSON.parse(additionalSettings) : undefined,
        meta.redactorType === 'cms' ? { cmsData: redactorSettings } : undefined
      )
    }
    setSaving(false)
  }

  const updateState = (key: TDetailState, value: unknown) => {
    if (key === 'pageWidget') {
      return setPageWidget(value as IPageWidget)
    }
    if (key === 'additionalSettings') {
      return setAdditionalSettings(value as string)
    }
    console.error('WidgetList/Detail: Try to set unknown state')
  }

  const additionalSet = JSON.parse(additionalSettings || '{}')
  const redactorFromMeta = meta.redactor
  const redactorType = meta.redactorType
  const WidgetSettingEditor = meta.widgetSettingsEditor

  return (
    <form onSubmit={onSubmit} className={styles.root} noValidate autoComplete="off">
      <TabButtons
        classes={{
          header: styles.tabsHeader,
        }}
        items={[
          {
            id: 'basicSettings',
            label: 'Basic settings',
            content: () => (
              <Controller>
                <BasicSettings
                  renderCustomSettingsWidget={props.renderCustomSettingsWidget}
                  onChangeField={onChangeField}
                  widget={widget}
                  roles={roles}
                />
              </Controller>
            ),
          },
          {
            id: 'editor',
            label: 'Editor',
            viewCond: !!redactorFromMeta && redactorType === 'cms',
            content: () => (
              <EditorSettings
                sitePage={sitePage}
                widget={widget}
                widgets={widgets}
                EditorFromMeta={redactorFromMeta}
                updateRedactorSettings={(newRedactorSettings) => {
                  setRedactorSettings(newRedactorSettings)
                }}
              />
            ),
          },
          {
            id: 'customSettings',
            label: 'Custom settings',
            viewCond: !!WidgetSettingEditor,
            content: () => (
              <>
                {!!WidgetSettingEditor && (
                  <WidgetSettingEditor
                    updateState={(newState = {}) => {
                      for (const key of Object.keys(newState)) {
                        const value = newState[key]
                        updateState(key as TDetailState, value)
                      }
                    }}
                    additionalSet={additionalSet}
                  />
                )}
              </>
            ),
          },

          {
            id: 'pageCustomize',
            label: 'Page customize',
            viewCond: props.hasAdditionalSettings,
            content: () => (
              <Controller>
                <CustomSettings
                  onChangeField={onChangeField}
                  widget={widget}
                  pageWidget={pageWidget}
                  additionalSet={additionalSet}
                  updateState={updateState}
                  additionalSettings={additionalSettings}
                  redactorFromMeta={redactorFromMeta}
                />
              </Controller>
            ),
          },
        ]}
      />

      <Tooltip
        className={styles.saveButton}
        tipContent={() => errorMessage || <></>}
        disabled={!errorMessage}
      >
        <Button onClick={onSubmit} loading={saving} disabled={!!errorMessage}>
          Save
        </Button>
      </Tooltip>
    </form>
  )
}
